import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Tools › Test RADIUS login (owner 2026-10-21; radaccess v1.134):
 *   POST /access/operator/{ns}/subscribers/{AccountID}/radius-test {username, password}
 * radaccess sends a real Access-Request to FreeRADIUS. The password is never
 * stored, logged or returned; 5 tests per subscriber per hour; every test is
 * audited (who, when, result).
 */

export type RadiusTestOutcome = "accept" | "reject" | "no_answer";

export interface RadiusTestCheck {
  label: string;
  ok: boolean;
  detail: string;
}

export interface RadiusTestResult {
  result: RadiusTestOutcome;
  reply_message: string;
  /** Reply attributes worth showing, "Name=value" (plan speed, timeouts). */
  attributes: string[];
  checks: RadiusTestCheck[];
  tests_left: number;
}

export async function testRadiusLogin(identity: CallerIdentity, ns: string, accountId: string, username: string, password: string): Promise<RadiusTestResult> {
  const r = await callGateway<RadiusTestResult>(
    identity,
    config().gatewayBase,
    `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}/radius-test`,
    { method: "POST", body: { username, password } },
  );
  const d = r.data;
  return {
    result: d?.result ?? "no_answer",
    reply_message: d?.reply_message ?? "",
    attributes: d?.attributes ?? [],
    checks: d?.checks ?? [],
    tests_left: d?.tests_left ?? 0,
  };
}

/** The one-line verdict staff read first. */
export function radiusTestHeadline(r: RadiusTestResult): { severity: "success" | "error" | "warning"; text: string } {
  switch (r.result) {
    case "accept":
      return { severity: "success", text: "Accepted: this username and password sign in." };
    case "reject": {
      const why = r.checks.filter((c) => !c.ok).map((c) => c.detail);
      return { severity: "error", text: why.length ? `Rejected: ${why.join("; ")}.` : "Rejected by RADIUS." };
    }
    default:
      return { severity: "warning", text: "RADIUS did not answer. Try again in a minute; if it keeps happening, tell support." };
  }
}
